import type { MailboxMessage } from '@compass/client/types/mailbox';
import { Bell, UserRoundCheck } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { useDeepLinkNavigation } from '@/hooks/useDeepLinkNavigation';
import {
  type MailboxEventType,
  useMailboxInbox,
  useMailboxNotifications,
  useMailboxUnreadCount,
  useUpdateMailboxFlags,
} from '@/hooks/useMailbox';
import { useWebNotification } from '@/hooks/useWebNotification';
import { cn } from '@/lib/utils';
import { mailboxService } from '@/services/api';
import { useStore } from '@/store';
import { getRelativeTime } from '@/utils/date';
import { buildUrlFromMetadata } from '@/utils/deeplink';
import { buildRoutePath } from '@/utils/routes';

const PREVIEW_LIMIT = 5;

export function NotificationBell() {
  const navigate = useNavigate();
  const { selectedOrganizationId, selectedProgramId, getTimeZone } = useStore();
  const timeZone = getTimeZone();
  const { showNotification } = useWebNotification();
  const { navigateToDeepLink } = useDeepLinkNavigation();

  const [open, setOpen] = useState(false);

  const { data: unreadData } = useMailboxUnreadCount();
  const unreadCount = unreadData?.unread_count ?? 0;

  // Fetch unread messages eagerly so data is cached before the popover opens.
  const { data: inboxData } = useMailboxInbox({ limit: PREVIEW_LIMIT, is_read: false });
  const messages = useMemo(() => inboxData?.messages ?? [], [inboxData?.messages]);

  const { mutate: updateFlags } = useUpdateMailboxFlags();

  // SSE events don't include metadata, so we fetch the full message on click
  // to get the deep-link URL. Using window.location.href avoids popup blockers
  // that would block window.open after an async call inside notification.onclick.
  const handleSSEEvent = useCallback(
    (type: MailboxEventType, data: Record<string, unknown>) => {
      if (type !== 'mailbox.message.received') return;

      const subject = data.subject as string | undefined;
      if (!subject) return;

      const messageId = data.message_id as string | undefined;

      showNotification('New notification', {
        body: subject,
        tag: messageId,
        onClick: messageId
          ? () => {
              mailboxService
                .getMailboxMessage({ message_id: messageId })
                .then(resp => {
                  const metadata = (resp?.message?.metadata ?? {}) as Record<string, unknown>;
                  const url = buildUrlFromMetadata(metadata);
                  if (url) {
                    window.location.href = url;
                  }
                })
                .catch(() => {
                  // Silently ignore — notification click won't navigate
                });
            }
          : undefined,
      });
    },
    [showNotification],
  );

  useMailboxNotifications(true, handleSSEEvent);

  const handleOpenMessage = useCallback(
    (msg: MailboxMessage) => {
      if (!msg.id) return;
      setOpen(false);
      if (!msg.is_read) {
        updateFlags({ messageId: msg.id, isRead: true });
      }
      navigateToDeepLink((msg.metadata ?? {}) as Record<string, unknown>);
    },
    [updateFlags, navigateToDeepLink],
  );

  const handleSeeAll = useCallback(() => {
    setOpen(false);
    if (selectedOrganizationId && selectedProgramId) {
      navigate(buildRoutePath('/mailbox', selectedOrganizationId, selectedProgramId));
    }
  }, [navigate, selectedOrganizationId, selectedProgramId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8" aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 h-4 min-w-4 justify-center rounded-full px-1 text-[10px] leading-none"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <span className="text-xs text-muted-foreground">{unreadCount} unread</span>
          )}
        </div>

        <Separator />

        <div className="max-h-[320px] overflow-y-auto">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">No unread notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {messages.map(msg => (
                <Button
                  key={msg.id}
                  variant="ghost"
                  className="flex w-full items-start gap-2.5 px-4 py-3 h-auto rounded-none text-left hover:bg-muted/50"
                  onClick={() => handleOpenMessage(msg)}
                >
                  <span
                    className={cn(
                      'mt-0.5 shrink-0 flex items-center justify-center size-7 rounded-lg',
                      msg.is_read ? 'bg-muted' : 'bg-primary/10',
                    )}
                  >
                    <UserRoundCheck
                      className={cn(
                        'size-3.5',
                        msg.is_read ? 'text-muted-foreground' : 'text-primary',
                      )}
                    />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        'text-sm whitespace-normal',
                        msg.is_read ? 'text-muted-foreground' : 'font-medium text-foreground',
                      )}
                    >
                      {msg.subject || '(No subject)'}
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {getRelativeTime(msg.created_at, timeZone)}
                    </span>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </div>

        <Separator />

        <div className="px-4 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-sm text-primary"
            onClick={handleSeeAll}
          >
            See all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
